import React, { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { loginUser } from "../../api/api";
import { adoptAccount } from "../../lib/sessionActions";
import { accountHomePath, getRoster } from "../../lib/sessionToken";
import { ViewIcon, ViewOffIcon } from "../../widgets/icons";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

/**
 * Accept only a same-site path so a login link cannot send someone off Assembly.
 * @param {string|null} value
 * @returns {string}
 */
function safeNextPath(value) {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//")) {
    return "";
  }
  if (value.includes("\\") || value.includes("://")) {
    return "";
  }
  return value;
}

/**
 * Login form over the shared hero map — glass card matches the landing CTA panel
 * so home → login feels continuous.
 * @param {Object} props
 * @param {(user: object) => void} props.stageSession Hold the account until the next route paints
 * @returns {JSX.Element}
 */
export default function Login({ stageSession }) {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [missingFields, setMissingFields] = useState({
    username: false,
    password: false,
  });
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const passwordResetNotice = Boolean(location.state?.passwordReset);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (missingFields[name] && value.trim()) {
      setMissingFields((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const nextMissing = {
      username: !formData.username.trim(),
      password: !formData.password,
    };
    setMissingFields(nextMissing);
    if (nextMissing.username || nextMissing.password) {
      return;
    }

    const result = await loginUser(formData.username, formData.password);
    if (result.error) {
      setError(result.error);
    } else {
      if (result?.data?.type === "admin") {
        setError("Invalid account type"); // admin tried to login as user (don't tell the user that for security)
        return;
      }
      const adopted = await adoptAccount(result.data, result.token || "");
      if (adopted.error) {
        setError(adopted.error);
        return;
      }
      stageSession(result.data);
      const nextPath = safeNextPath(searchParams.get("next"));
      navigate(nextPath || accountHomePath(result.data?.type));
    }
  };

  return (
    <main className="relative z-10 overflow-x-hidden text-text-primary">
      <section className="relative flex min-h-[calc(100dvh-3.5rem)] w-full flex-col items-center justify-center gap-8 overflow-x-hidden px-4 py-12 text-center md:gap-10 md:py-16">
        <div className="relative mx-auto w-full max-w-4xl">
          <div className="mx-auto w-full max-w-md rounded-2xl border border-hero-glass-border bg-hero-glass-bg px-6 py-8 shadow-staff-card backdrop-blur-sm md:max-w-lg md:px-10 md:py-10">
            <h1 className="mb-8 text-3xl font-bold text-hero-heading md:text-4xl">
              {getRoster().accounts.length ? "Add account" : "Login to Assembly"}
            </h1>

            {passwordResetNotice ? (
              <p
                role="status"
                className="mb-4 rounded-md border border-hero-glass-border bg-hero-glass-bg px-3 py-2 text-left text-sm text-hero-heading backdrop-blur-3xl"
              >
                Password updated. Log in with your new password.
              </p>
            ) : null}

            {error ? (
              <p
                role="alert"
                className="mb-4 rounded-md border border-error bg-[var(--color-error-bg)] px-3 py-2 text-left text-sm text-error backdrop-blur-3xl"
              >
                {error}
              </p>
            ) : null}

            <form
              onSubmit={handleSubmit}
              noValidate
              className="space-y-5 text-left"
            >
              <div className="space-y-2">
                <Label
                  htmlFor="username"
                  className="text-hero-subheading"
                >
                  Username or email
                  {missingFields.username ? (
                    <span className="ml-1 font-medium text-error" aria-live="polite">
                      * required
                    </span>
                  ) : null}
                </Label>
                <Input
                  type="text"
                  name="username"
                  id="username"
                  aria-required="true"
                  aria-invalid={missingFields.username}
                  autoComplete="username"
                  autoFocus
                  value={formData.username}
                  onChange={handleChange}
                  className="h-11 border-hero-glass-border bg-hero-glass-bg text-hero-heading shadow-none backdrop-blur-sm focus-visible:ring-ui-ring focus-visible:ring-offset-transparent"
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="password"
                  className="text-hero-subheading"
                >
                  Password
                  {missingFields.password ? (
                    <span className="ml-1 font-medium text-error" aria-live="polite">
                      * required
                    </span>
                  ) : null}
                </Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    id="password"
                    aria-required="true"
                    aria-invalid={missingFields.password}
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleChange}
                    className="h-11 border-hero-glass-border bg-hero-glass-bg pr-11 text-hero-heading shadow-none backdrop-blur-sm focus-visible:ring-ui-ring focus-visible:ring-offset-transparent"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-hero-heading hover:text-accent focus:outline-none focus-visible:text-accent"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? (
                      <ViewOffIcon size={20} />
                    ) : (
                      <ViewIcon size={20} />
                    )}
                  </button>
                </div>
                <p className="text-right">
                  <Link
                    to="/forgot-password"
                    className="text-sm font-semibold text-hero-heading underline decoration-hero-heading decoration-2 underline-offset-[5px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                  >
                    Forgot password?
                  </Link>
                </p>
              </div>

              <Button
                type="submit"
                className="h-12 w-full rounded-full bg-hero-cta-primary px-8 font-semibold text-hero-cta-primary-fg shadow-lg transition-[transform,box-shadow] duration-200 ease-out hover:bg-hero-cta-primary-hover motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-xl active:scale-[0.98]"
              >
                Login
              </Button>
            </form>

            <p className="mt-6 text-sm text-hero-subheading">
              New here?{" "}
              <Link
                to="/create-account"
                className="font-semibold text-hero-heading underline decoration-hero-heading decoration-2 underline-offset-[5px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
              >
                Create account
              </Link>
            </p>
          </div>
        </div>

        <div className="relative flex w-full max-w-4xl justify-center px-2">
          <Link
            to="/explore"
            className="group text-base font-semibold text-hero-heading focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-transparent md:text-lg"
          >
            <span className="underline decoration-hero-heading decoration-2 underline-offset-[5px]">
              Explore Map{" "}
              <span
                aria-hidden
                className="inline-block translate-y-px text-xl leading-none transition-transform motion-safe:group-hover:translate-x-1"
              >
                →
              </span>
            </span>
          </Link>
        </div>
      </section>
    </main>
  );
}
